export const ClientType = {
  Soloist: 'Soloist',
  Vocalist: 'Vocalist',
  Ensemble: 'Ensemble',
  Quartet: 'Quartet',
  Church: 'Church',
  Festival: 'Festival',
  Organization: 'Organization',
  Publication: 'Publication',
  Coach: 'Coach',
  Educator: 'Educator',
  University: 'University',
  School: 'School',
  Venue: 'Venue',
  Composer: 'Composer',
  Conductor: 'Conductor',
  Artist: 'Artist',
  ArtsOrganization: 'Arts Organization',
  NonProfit: 'Non-Profit',
  MusicSchool: 'Music School',
  Conservatory: 'Conservatory',
  PerformingArtsCenter: 'Performing Arts Center',
  Organist: 'Organist',
} as const

export type ClientType = (typeof ClientType)[keyof typeof ClientType]

/**
 * Top-level portfolio category. Drives the filter tabs on /portfolio.
 * Entries without an explicit category are treated as 'Websites'
 * (the overwhelming majority), so only Photography/Graphic Design
 * entries need to set it.
 */
export const PortfolioCategory = {
  Websites: 'Websites',
  Photography: 'Photography',
  GraphicDesign: 'Graphic Design',
} as const

export type PortfolioCategory = (typeof PortfolioCategory)[keyof typeof PortfolioCategory]

/** Category order for the filter tabs. */
export const PORTFOLIO_CATEGORIES: PortfolioCategory[] = [
  PortfolioCategory.Websites,
  PortfolioCategory.Photography,
  PortfolioCategory.GraphicDesign,
]

export interface ProjectMetric {
  value: string
  label: string
  description?: string
}

export interface ProjectTestimonial {
  quote: string
  author: string
  role?: string
}

export interface BeforeAfterPair {
  before: string
  after: string
  label?: string
}

/**
 * A detail-page gallery image. A bare string is the full-size src; the object
 * form lets a grid render a lighter `thumb` while the lightbox loads `src`.
 */
export type GalleryImage = string | { src: string; thumb?: string }

export interface Project {
  slug: string
  title: string
  description: string
  image: string
  /** CSS object-position for the card thumbnail crop; defaults to 'center' */
  imagePosition?: string
  url: string
  /** Top-level portfolio category; defaults to 'Websites' when omitted. */
  category?: PortfolioCategory
  clientTypes?: ClientType[]
  client?: string
  summary?: string
  objectives?: string[]
  implementation?: string[]
  results?: string[]
  images?: GalleryImage[]
  beforeAfter?: BeforeAfterPair[]
  metrics?: ProjectMetric[]
  testimonial?: ProjectTestimonial
  serviceTypes?: string[]
}

export const fugueLabProjects: Project[] = [
  {
    slug: 'sierra-canyon-photo-competition',
    title: 'Sierra Canyon Photo Competition',
    description:
      'Photography competition platform for Sierra Canyon School with student submissions, teacher moderation, and anonymous voting.',
    image: '/portfolio/sierra-canyon-photo.png',
    url: 'https://scsphotocomp.org',
    clientTypes: [ClientType.School],
    client: 'Sierra Canyon School — Visual Arts Department',
    summary:
      'Built a complete photography competition platform enabling students to submit photos, teachers to moderate submissions, and the school community to vote anonymously. Replaced a manual paper-based process with a streamlined digital experience.',
    objectives: [
      'Replace paper submission process with digital workflow',
      'Enable teacher moderation before public voting',
      'Create anonymous voting system for fair competition',
      'Support mobile-first experience for student submissions',
      'Handle HEIC photo uploads from iPhones',
    ],
    implementation: [
      'Multi-step submission wizard with photo upload and metadata',
      'Admin dashboard for reviewing and approving submissions',
      'Magic link authentication for anonymous voting',
      'ELO and classic voting modes',
      'Real-time results dashboard',
      'Automated email notifications (acceptance, rejection, voting links)',
      'HEIC to JPEG conversion for iOS compatibility',
    ],
    results: [
      'Launched for 2025-2026 school year',
      'Reduced teacher review workflow from 4+ hours to 45 minutes',
      'Mobile-friendly submission process for students',
      '100% digital, zero paper forms',
      'Automated email notifications eliminated manual follow-ups',
    ],
    metrics: [
      { value: '2 weeks', label: 'Built In', description: 'From kickoff to launch' },
      { value: '85%', label: 'Time Saved', description: 'Teacher review workflow' },
      { value: '0', label: 'Paper Forms', description: 'Fully digital process' },
    ],
    testimonial: {
      quote:
        'Garrett did such a great job making a user-friendly submission and voting app for our photo competition. It was accessible for all ages and easy to use! He was helpful every step of the way and implemented feedback flawlessly.',
      author: 'Sierra Canyon Visual Arts Department',
      role: 'Art Teacher, Sierra Canyon School',
    },
  },
  {
    slug: 'chroma-music-festival',
    title: 'Chroma International Music Festival',
    description:
      'Website for a new international music festival launching in Hungary, featuring programs for piano and strings. Built for early credibility, participant info, and long-term growth.',
    image: '/portfolio/chroma-festival.jpeg',
    url: 'https://chromafestival.vercel.app',
    clientTypes: [ClientType.Festival],
    client: 'Chroma International Music Festival',
    summary:
      'Built the digital presence for a new international music festival launching in Miskolc, Hungary in 2026. The site establishes credibility with prospective participants, faculty, and sponsors while providing clear information about programs and the application process.',
    objectives: [
      'Establish credibility for a brand-new festival',
      'Attract international participants from multiple countries',
      'Showcase piano and strings programs',
      'Provide clear application and enrollment information',
      'Build anticipation for the inaugural season',
    ],
    implementation: [
      'Bilingual content structure for international reach',
      'Faculty and artistic leadership profiles',
      'Program details for piano and strings tracks',
      'Online application integration',
      'Mobile-responsive design for global accessibility',
      'Photo galleries showcasing the Miskolc venue and region',
    ],
    results: [
      'Launching inaugural season in Summer 2026',
      'Attracting participants from 5+ countries (China, USA, France, Italy, and beyond)',
      'Application system processed inquiries from day one',
      'Professional platform secured sponsor partnerships before launch',
      'Site established credibility that drove early faculty recruitment',
    ],
    metrics: [
      { value: '5+', label: 'Countries', description: 'International participant reach' },
      { value: '4 weeks', label: 'Built In', description: 'Full site + application system' },
      { value: '2', label: 'Programs', description: 'Piano and strings tracks' },
    ],
    testimonial: {
      quote:
        'Garrett understood exactly what we needed as a new festival. The site gave us instant credibility with sponsors, faculty, and prospective participants.',
      author: 'Chroma Festival',
      role: 'Founding Team',
    },
  },
  {
    slug: 'chroma-logo-concepts',
    title: 'Chroma Festival — Logo Exploration',
    description:
      'Brand identity exploration for an international music festival. These concept designs feature an innovative 11-color chromatic brandmark, beautifully connecting the festival name "Chroma" with the 11 notes of the chromatic scale.',
    image: '/portfolio/graphic-design/chroma-03.png',
    url: '',
    category: PortfolioCategory.GraphicDesign,
    clientTypes: [ClientType.Festival],
    client: 'Chroma International Music Festival',
    summary:
      'Developed brand identity concepts for the Chroma International Music Festival, exploring visual connections between the festival name and musical chromatic scale. The 11-color brandmark represents the 11 notes of the chromatic scale.',
    objectives: [
      'Create memorable visual identity for new festival',
      'Connect "Chroma" name to musical meaning',
      'Develop versatile mark for print and digital use',
      'Establish sophisticated, international aesthetic',
    ],
    implementation: [
      'Multiple concept directions explored',
      '11-color chromatic brandmark design',
      'Typography and color palette development',
      'Versatile mark for various applications',
    ],
    results: [
      'Strong brand foundation for festival launch',
      'Unique visual identity in festival space',
      'Meaningful connection to musical heritage',
    ],
    images: [
      '/portfolio/graphic-design/chroma-01.png',
      '/portfolio/graphic-design/chroma-03.png',
      '/portfolio/graphic-design/chroma-02.png',
    ],
    serviceTypes: ['Brand Identity', 'Logo Design', 'Logo Package'],
  },
  {
    slug: 'chroma-festival-photography',
    title: 'Chroma International Music Festival — Photography',
    description:
      'Event photography from the inaugural Chroma International Music Festival in Miskolc, Hungary — group portraits and candid moments used across the festival website, social channels, and promotional material.',
    image: '/portfolio/photography/DSCF2893-thumb.webp',
    url: '',
    category: PortfolioCategory.Photography,
    clientTypes: [ClientType.Festival],
    client: 'Chroma International Music Festival',
    summary:
      'On-site event photography for the festival’s first season. Group portraits and candid moments, edited and delivered web-ready for the festival’s website, newsletter, and social channels.',
    serviceTypes: ['Event Photography', 'Portraiture'],
    images: [
      { src: '/portfolio/photography/DSCF2883.webp', thumb: '/portfolio/photography/DSCF2883-thumb.webp' },
      { src: '/portfolio/photography/DSCF2893.webp', thumb: '/portfolio/photography/DSCF2893-thumb.webp' },
      { src: '/portfolio/photography/DSCF2894.webp', thumb: '/portfolio/photography/DSCF2894-thumb.webp' },
      { src: '/portfolio/photography/DSCF2898.webp', thumb: '/portfolio/photography/DSCF2898-thumb.webp' },
      { src: '/portfolio/photography/DSCF2899.webp', thumb: '/portfolio/photography/DSCF2899-thumb.webp' },
      { src: '/portfolio/photography/DSCF2901.webp', thumb: '/portfolio/photography/DSCF2901-thumb.webp' },
      { src: '/portfolio/photography/DSCF2906.webp', thumb: '/portfolio/photography/DSCF2906-thumb.webp' },
      { src: '/portfolio/photography/DSCF2907.webp', thumb: '/portfolio/photography/DSCF2907-thumb.webp' },
      { src: '/portfolio/photography/DSCF2910.webp', thumb: '/portfolio/photography/DSCF2910-thumb.webp' },
      { src: '/portfolio/photography/DSCF2911.webp', thumb: '/portfolio/photography/DSCF2911-thumb.webp' },
      { src: '/portfolio/photography/DSCF2915.webp', thumb: '/portfolio/photography/DSCF2915-thumb.webp' },
      { src: '/portfolio/photography/DSCF2929.webp', thumb: '/portfolio/photography/DSCF2929-thumb.webp' },
      { src: '/portfolio/photography/DSCF2932.webp', thumb: '/portfolio/photography/DSCF2932-thumb.webp' },
    ],
  },
  {
    slug: 'fugue-lab-brand-identity',
    title: 'Fugue Lab — Brand Identity',
    description:
      'Our own brandmark: a laboratory flask with musical notes rising like vapor. An example of the logo and brand packages we design for clients — built to work across light and dark surfaces.',
    image: '/portfolio/graphic-design/fugue-lab-logo-on-navy-thumb.webp',
    url: '',
    category: PortfolioCategory.GraphicDesign,
    client: 'Fugue Lab',
    summary:
      'A wordmark-plus-mark lockup that pairs a chemistry flask with rising musical notes — craft and music in one symbol. Delivered as a full logo package: primary lockup, standalone mark, and light/dark variants for every surface.',
    serviceTypes: ['Brand Identity', 'Logo Design', 'Logo Package'],
    images: [
      {
        src: '/portfolio/graphic-design/fugue-lab-logo-on-navy.webp',
        thumb: '/portfolio/graphic-design/fugue-lab-logo-on-navy-thumb.webp',
      },
      {
        src: '/portfolio/graphic-design/fugue-lab-logo-on-light.webp',
        thumb: '/portfolio/graphic-design/fugue-lab-logo-on-light-thumb.webp',
      },
    ],
  },
  {
    slug: 'brass-city-jazz-fest',
    title: 'Brass City Jazz Fest',
    description:
      'A bespoke, after-hours "jazz club" site for a free outdoor jazz festival in Waterbury, CT, built fully CMS-editable on the Fugue Lab platform.',
    image: '/portfolio/brass-city-jazz.jpg',
    url: 'https://brasscityjazzfest.fuguelab.app',
    clientTypes: [ClientType.Festival],
    client: 'Brass City Jazz Fest (Demo Preview)',
    summary:
      'Rebuilt the site for Brass City Jazz Fest, a free annual outdoor jazz festival in Downtown Waterbury, CT, as a bespoke hand-coded build with a late-night jazz-club mood. The cinematic homepage, marquee-style lineup board, full festival logistics, sponsor tiers, and vendor program are all editable in the prod CMS, with the design never depending on it.',
    objectives: [
      'Replace a dated festival site with a distinctive, after-hours visual identity',
      'Bill the 2026 lineup like a live marquee instead of a flat list',
      'Make festival logistics (date, location, hotel, volunteering) easy to find and share',
      'Present sponsor tiers and the vendor program with clear inquiry paths',
      'Hand the organizers a site their team can edit themselves through the CMS',
    ],
    implementation: [
      'Bespoke Vite + React build on the @gjl/site-kit SSR runtime, prerendered per route (SSG) for crawlable HTML',
      'Deep midnight-espresso surface with a single burnished brass-gold accent pulled from the festival logo',
      'DM Serif Display over Hanken Grotesk; signature "Marquee Lineup Board" with a glowing stage-bulb rail',
      'Cinematic scroll-reveal homepage, full artist cards, and a past-headliner history section',
      'Festival/Event JSON-LD, sitemap, robots, and distinct per-route canonicals',
      'CMS-editable surface (hero, intro, lineup, festival info, sponsors) via the seed + publish pipeline',
      'WebP imagery sized to the box; mailto-fallback vendor inquiry form for no-JS submission',
    ],
    results: [
      'Live, CMS-editable six-page site graded 94/100 (A) on the rendered quality audit',
      'Lineup, festival info, sponsors, and vendor content all server-rendered and indexable',
      'A distinctive after-hours identity that reads as a custom festival brand, not a template',
      'Sub-0.2s first paint, zero layout shift, on a 282KB / 10-request homepage',
    ],
    metrics: [
      { value: '6', label: 'Pages', description: 'Home, Lineup, Festival Info, Sponsors, Vendors, About' },
      { value: '94/100', label: 'Quality Score', description: 'Rendered A grade across all routes' },
      { value: 'Free', label: 'Admission', description: 'Community-first festival mission' },
    ],
  },
  {
    slug: 'coastal-chamber-musicians',
    title: 'Coastal Chamber Musicians',
    description:
      'Website for a Charleston-based string ensemble specializing in weddings and events, with audio samples, testimonials, repertoire, and quote request forms.',
    image: '/portfolio/coastal-chamber-musicians.jpg',
    imagePosition: '15% center',
    url: 'https://coastalchambermusicians.fuguelab.app',
    clientTypes: [ClientType.Ensemble],
    client: 'Coastal Chamber Musicians (Demo Preview)',
    summary:
      'Built a complete website for a Charleston, SC string ensemble with 15+ years of wedding and event experience. The site showcases their music through embedded videos and audio recordings, features client testimonials, and includes a detailed quote request form for booking inquiries.',
    objectives: [
      'Create a professional platform for booking inquiries',
      'Showcase performance videos and audio recordings',
      'Display client testimonials and award badges',
      'Provide a detailed quote request form',
      'Present full repertoire list across genres',
    ],
    implementation: [
      'Warm, romantic theme with burgundy and gold palette',
      'Embedded YouTube performance videos',
      'Audio player with 10 sample recordings',
      'Testimonials carousel with GigSalad reviews',
      'Multi-field quote request form (event type, instrumentation, date)',
      'Musician bios with photos and credentials',
      'Comprehensive repertoire list (classical, jazz, pop, folk, sacred)',
      'Award badge gallery (WeddingWire Couples Choice)',
    ],
    results: [
      'Professional booking platform replacing basic site',
      'Audio and video samples reduce pre-booking questions',
      'Quote form captures detailed event information upfront',
      'Award badges build immediate credibility',
    ],
    metrics: [
      { value: '15+', label: 'Years', description: 'Wedding and event experience' },
      { value: '7', label: 'Pages', description: 'Home, About, Videos, Recordings, Repertoire, Quote, Contact' },
      { value: '10', label: 'Audio Tracks', description: 'Sample recordings with player' },
    ],
    beforeAfter: [
      {
        before: '/portfolio/coastal-chamber-before.png',
        after: '/portfolio/coastal-chamber-after.png',
        label: 'Homepage',
      },
    ],
  },
  {
    slug: 'new-school-of-music',
    title: 'New School of Music',
    description:
      "A color-forward, jazz-handbill rebuild for Austin's nonprofit community music school, built bespoke and fully CMS-editable on the Fugue Lab platform.",
    image: '/portfolio/new-school-of-music.jpg',
    url: 'https://newschoolofmusicnsmaustin.fuguelab.app',
    clientTypes: [ClientType.MusicSchool, ClientType.NonProfit],
    client: 'New School of Music (Demo Preview)',
    summary:
      "Rebuilt the site for the New School of Music, Austin, the city's first community music school (established 1982). The bespoke build leads with the school's scholarship mission and gives the Texas Jazz & Blues Camp, weekly Jazz Clubs, and lesson programs their own pages, with every section editable in the prod CMS.",
    objectives: [
      'Put the scholarship mission front and center, where it earns the most trust',
      'Give the Texas Jazz & Blues Camp and weekly Jazz Clubs their own clear pages',
      'Present all 18 faculty and staff with full roles and instruments',
      'Make lessons, areas of study, and registration easy to find',
      'Hand the all-volunteer nonprofit a site their team can edit through the CMS',
    ],
    implementation: [
      'Bespoke Vite + React build on the @gjl/site-kit SSR runtime, prerendered per route (SSG) for crawlable HTML',
      'Color-forward Austin jazz-handbill identity: stage-cream banded with deep teal and hot-orange accents',
      'Anton condensed poster display over DM Sans; signature gig-poster stacked-type motif + staggered instrument tiles',
      'Seven pages with verbatim content: Home, About, Lessons, Jazz Clubs, Texas Jazz & Blues Camp, Donate, Contact',
      'MusicSchool JSON-LD, sitemap, robots, and distinct per-route canonicals',
      'CMS-editable surface (hero, intro, mission) via the seed + publish pipeline; mailto-fallback contact form',
    ],
    results: [
      'Live, CMS-editable site graded 94/100 (A) on the rendered quality audit',
      'Scholarship work (50% of enrollment, 7,000+ lessons, $245K+ impact) leads the homepage',
      'Programs and faculty server-rendered and indexable, replacing an AJAX-thin original',
      'A distinctive, color-forward identity that reads as a custom build, not a template',
    ],
    metrics: [
      { value: '7', label: 'Pages', description: 'Home, About, Lessons, Jazz Clubs, Camp, Donate, Contact' },
      { value: '18', label: 'Faculty & Staff', description: 'Full roles, instruments, and bios' },
      { value: '94/100', label: 'Quality Score', description: 'Rendered A grade across all routes' },
    ],
  },
  {
    slug: 'atlantic-chamber-ensemble',
    title: 'Atlantic Chamber Ensemble',
    description:
      'A modern-editorial concert-poster site for a Richmond, VA new-music chamber collective, built bespoke yet fully CMS-editable.',
    image: '/portfolio/atlantic-chamber-ensemble.jpg',
    url: 'https://atlanticchamberensemble.fuguelab.app',
    clientTypes: [ClientType.Ensemble],
    client: 'Atlantic Chamber Ensemble (Demo Preview)',
    summary:
      'The Atlantic Chamber Ensemble is a dynamic group of professional musicians based in Richmond, Virginia, drawn from the Richmond Symphony and artist faculty at institutions like VCU, William & Mary, LSU, and the University of Oklahoma. A 501(c)(3) founded in 2011, ACE commissions and programs music seldom heard, performing in houses, churches, galleries, and off-the-beaten-path venues. This bespoke demo replaces their dated Wix site with a dark, editorial "concert poster" design while keeping every section editable in the Fugue Lab CMS.',
    objectives: [
      'Replace an aging, template-feeling Wix site with a distinctive, brand-true visual identity',
      'Foreground ACE’s commissioning and new-music mission rather than a generic ensemble layout',
      'Present the Hearsay album and its program in a way that invites listening across streaming platforms',
      'Give all 10 musicians proper bio pages and make the roster owner-editable without breaking the design',
      'Ship a fast, accessible, search-optimized site with structured data for the group and its members',
    ],
    implementation: [
      'Bespoke hand-coded Vite + React site on the @gjl/site-kit SSG runtime, prerendered per route with canonical tags, sitemap, and robots',
      'Modern-editorial "concert poster" theme: near-black ink canvas with a single acid-chartreuse signal, Space Grotesk display + Newsreader body serif',
      'Signature numbered editorial ledger driving the Hearsay tracklist and the past-projects archive',
      'JSON-LD MusicGroup structured data listing all 10 members; duotone photo treatment to unify mixed-quality source photos',
      'CMS-editable surface seeded via @gjl/cms: home hero, intro lede, 10 musician bios, Hearsay tracklist, and the projects archive',
      'Fail-closed content gate with per-block static fallbacks so an editor can change copy without breaking the layout',
      'Optimized WebP image variants served from S3 + CloudFront; CMS publish owns the site’s S3 prefix',
    ],
    results: [
      'Rendered quality audit scored 98/100 (A), with accessibility, SEO, content, and structured data all at 100',
      'Seven full pages mapped 1:1 to the live site, each prerendered for fast first paint and clean indexing',
      'All 10 musician bios, the full Hearsay program, and the projects archive made owner-editable through the CMS',
      'A clearly differentiated, premium identity that reads as ACE’s own, not a template, across desktop and mobile',
    ],
    metrics: [
      {
        value: '7',
        label: 'Pages',
        description: 'Home, Concerts, Past Projects, Recordings, Musicians, Gallery, Contact',
      },
      {
        value: '10',
        label: 'Musicians',
        description: 'Richmond Symphony and university faculty, each with a full bio',
      },
      { value: '1 day', label: 'Built In', description: 'Automated demo site pipeline' },
    ],
  },
  {
    slug: 'north-valley-chorale',
    title: 'North Valley Chorale',
    description:
      'A warm, typographic rebuild for a 60-voice all-ages community chorus in Phoenix, built around their own tagline "the choir that cares" and their senior, memory-care, and veteran outreach.',
    image: '/portfolio/north-valley-chorale.jpg',
    url: 'https://northvalleychorale.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'North Valley Chorale (Demo Preview)',
    summary:
      'The North Valley Chorale is a 501(c)(3) community chorus of more than 60 singers, ages 18 to 85, in metropolitan Phoenix, Arizona. Founded in 2008 and led by Artistic Director Eleanor Johnson, the all-volunteer choir performs affordable concerts spanning classical, Broadway, patriotic, and sacred music, and brings choral music to memory-care residents, seniors, and veterans through its outreach program. This bespoke demo replaces their cluttered stock-WordPress site with a warm, type-led design while keeping every section editable in the Fugue Lab CMS.',
    objectives: [
      'Lead with their own line, "the choir that cares," and the outreach mission that gives the choir its name',
      'Replace a 25-plus-item menu with a focused seven-page structure built around the music and the 2026 season',
      'Make the free, all-ages concert season easy to find, read, and share',
      'Carry the choir without a photo library by leaning on type, color, and embedded performance video',
      'Hand the all-volunteer board a site they can edit themselves through the Fugue Lab CMS',
    ],
    implementation: [
      'Custom warm, type-led theme: garnet and gold on warm sand, chosen for an intergenerational community choir rather than a stock template',
      'Petrona display type paired with Figtree body, with the tagline set as a recurring warm-italic refrain',
      'Signature hand-drawn "songline" swash, a loose sung phrase that underlines the tagline and section heads',
      'Typography-led centered-statement hero plus embedded YouTube performances, since only one real photograph existed',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap, robots, and JSON-LD',
      'Seven routes: Home, About, Concerts, Outreach, Watch & Listen, Leadership, and Join',
    ],
    results: [
      'Seven fully prerendered routes scoring 97/100 (A) on the rendered quality audit, against the current site’s 63/100 (D)',
      'Accessibility lifted from 0 to 100, plus social-share tags and event structured data the old site lacked',
      'A distinctive, warm identity that foregrounds the choir’s music and outreach instead of a generic ensemble template',
      'A CMS-editable platform the volunteer board can update without touching design or code',
    ],
    metrics: [
      { value: '7', label: 'Pages', description: 'Home, About, Concerts, Outreach, Watch & Listen, Leadership, Join' },
      { value: '63 → 97', label: 'Quality Score', description: 'Rendered grade, D to A, vs the current site' },
      { value: '1 day', label: 'Built In', description: 'Automated demo site pipeline' },
    ],
  },
  {
    slug: 'wendell-whalum-community-chorus',
    title: 'Wendell P. Whalum Community Chorus',
    description:
      'A heritage-led rebuild for a historic Atlanta community chorus, surfacing six decades of legacy that their "under construction" GoDaddy page had buried.',
    image: '/portfolio/wpwcc.jpg',
    url: 'https://wpwcc.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'Wendell P. Whalum Community Chorus (Demo Preview)',
    summary:
      'The Wendell P. Whalum Community Chorus is a historic community chorus in Atlanta, founded in 1966 as the Atlanta University Center Community Chorus and led today by Dr. David Morrow, director of the Morehouse College Glee Club. Over six decades it has sung at President Carter\'s inauguration, for Nelson Mandela, at the 1996 Olympic Cultural Olympiad, and at Carnegie Hall. This bespoke demo replaces their placeholder "under construction" page with a dignified, heritage-led design, every section editable in the Fugue Lab CMS.',
    objectives: [
      "Surface the chorus's storied 60-year history, which the current site buries entirely",
      'Carry the dignity of a Morehouse and Atlanta University Center choral institution, not a stock template',
      'Make their free Greater Atlanta concerts and their music easy to find and share',
      'Hand the chorus a site they can edit themselves through the Fugue Lab CMS',
    ],
    implementation: [
      'Custom dark "Heritage Program" theme: espresso-aubergine, antique gold, and oxblood, set in DM Serif Display and Karla',
      "Signature Heritage Timeline, a gold-spined chronology of the chorus's milestones from 1966 to Carnegie Hall",
      'Photo-band hero on the full ensemble that fades into a solid title band, keeping every face clear',
      "Content drawn verbatim from the chorus's own history, with the founder and Dr. Morrow's full biography",
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap, robots, and JSON-LD',
      'Nine routes: Home, Our Story, The Music, Performances, Sing With Us, Support, Contact, plus Privacy and Accessibility',
    ],
    results: [
      'Nine fully prerendered routes scoring 95/100 (A) on the rendered quality audit, with accessibility at 100',
      "A dignified heritage identity that foregrounds the chorus's legacy instead of a generic ensemble template",
      'Social-share tags, structured data, and a sitemap the placeholder site lacked',
      'A CMS-editable platform the chorus can update without touching design or code',
    ],
    metrics: [
      {
        value: '9',
        label: 'Pages',
        description: 'Home, Our Story, The Music, Performances, Sing With Us, Support, Contact + legal',
      },
      { value: '95/100', label: 'Quality', description: 'Rendered grade (A), with accessibility at 100' },
      { value: 'Since 1966', label: 'Heritage', description: 'Six decades surfaced in a Heritage Timeline' },
    ],
  },
  {
    slug: 'glenn-memorial-music',
    title: 'Glenn Memorial Music Ministry',
    description:
      'A bespoke site for a six-choir, cradle-to-senior church music ministry on Emory’s campus, replacing a music section that had been frozen since 2021.',
    image: '/portfolio/glenn-memorial.jpg',
    url: 'https://glennmemorial.fuguelab.app',
    clientTypes: [ClientType.Church, ClientType.NonProfit],
    client: 'Glenn Memorial Music Ministry (Demo Preview)',
    summary:
      'Glenn Memorial United Methodist Church sits in the historic Druid Hills neighborhood on Emory University’s campus in Atlanta. Its music ministry, led by Director of Music and Worship Arts Michael Dauterman, runs six choirs from pre-kindergarten through senior adult, a youth handbell choir, and the Emory Choral Scholars. This bespoke demo lifts the music ministry out of a church site whose music pages had been frozen since 2021, and gives the full program a dignified, current home, every section editable in the Fugue Lab CMS.',
    objectives: [
      'Show the whole six-choir program, which a stale music page had been hiding',
      'Carry the dignity of a historic neoclassical sanctuary, not a stock church template',
      'Make the choirs, the concerts, and the Music Fund easy to find and share',
      'Hand the music staff a site they can keep current themselves',
    ],
    implementation: [
      'Custom “Voices for Every Age” theme: warm ivory and deep sacramental navy with antique brass, set in Sorts Mill Goudy and Mulish',
      'Signature Choir Ladder, the six choirs rendered as one cradle-to-senior progression from the Cherub Choir to the Chancel Choir',
      'A cinematic hero on the church’s own neoclassical sanctuary, fading into the page',
      'Content drawn verbatim from the church’s pages, with the dated COVID-era guidance removed',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap, robots, and JSON-LD',
      'Nine routes: Home, The Choirs, Our Musicians, Concerts, Worship, The Music Fund, Contact, plus Privacy and Accessibility',
    ],
    results: [
      'Nine fully prerendered routes scoring 98/100 (A) on the rendered quality audit, with accessibility at 100',
      'A dignified, collegiate identity that finally shows the full cradle-to-senior program',
      'Social-share tags, structured data, and a sitemap the dated site lacked',
      'A CMS-editable platform the music staff can update without touching design or code',
    ],
    metrics: [
      { value: '6', label: 'Choirs', description: 'Cradle to senior, Pre-K through senior adult' },
      { value: '98/100', label: 'Quality', description: 'Rendered grade (A), with accessibility at 100' },
      { value: '9', label: 'Pages', description: 'Choirs, Musicians, Concerts, Worship, Music Fund, Contact + legal' },
    ],
  },
  {
    slug: 'north-carolina-youth-orchestra',
    title: 'North Carolina Youth Orchestra',
    description:
      'A concert-hall redesign for a Charlotte and Lake Norman youth orchestra, built on the Fugue Lab CMS-editable platform and showcasing their own concert photography.',
    image: '/portfolio/north-carolina-youth-orchestra.jpg',
    url: 'https://ncyo.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'North Carolina Youth Orchestra (Demo Preview)',
    summary:
      'The North Carolina Youth Orchestra is a nonprofit youth music institution serving the Charlotte and Lake Norman region, with a complete ladder of ensembles from beginning classes up to a full orchestra and a faculty drawn from the Charlotte Symphony and area conservatories. We rebuilt their site around a concert-hall idea, the lights coming up on a young musician, replacing a slow, dated site with a fast, photo-rich home that finally shows off their own concert photography.',
    objectives: [
      'Replace a slow, hard-to-use site (it failed basic accessibility and took seconds to load) with a fast, modern home',
      'Lead with the orchestra’s own concert photography instead of a sparse, generic template',
      'Make the ensemble ladder, from beginner strings up to the top orchestra, clear and easy to follow',
      'Foreground the Musical Pathways Initiative and the free Instrument Discovery Days for elementary students',
      'Give all-volunteer staff a site they can edit themselves through the Fugue Lab CMS',
    ],
    implementation: [
      'Concert-hall dark theme: a deep stage-indigo canvas with a single concert-gold accent used as punctuation',
      'Playfair Display paired with Space Grotesk, a cinematic full-bleed spotlight hero, and a program-ladder signature from beginner ensembles up to the top orchestra',
      'A masonry gallery of their own concert photography (natural-aspect tiles, faces never cropped) harvested from their media library',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'CMS content seam via useSite() with each block independently falling back to compiled-in static content',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap, robots, and MusicGroup structured data',
      'Nine routes: Home, About, Programs, Faculty, Events, Musical Pathways, Support, Contact, and a dedicated gallery',
    ],
    results: [
      'A 95-to-99 (A) rendered quality score, up from a failing grade on the current site',
      'Accessibility, SEO, content, and structured data all at 100',
      'The orchestra’s own concert photography finally shown off across the hero, page headers, and a dedicated gallery',
      'A CMS-editable platform the volunteer staff can update without touching design or code',
    ],
    metrics: [
      {
        value: '9',
        label: 'Pages',
        description: 'Home, About, Programs, Faculty, Events, Musical Pathways, Support, Contact, Gallery',
      },
      { value: '95/100', label: 'Quality Score', description: 'Rendered A grade, up from a failing current site' },
      {
        value: '13',
        label: 'Faculty',
        description: 'Charlotte Symphony principals and conservatory-trained teachers, each with a bio',
      },
    ],
  },
  {
    slug: 'durham-medical-orchestra',
    title: 'Durham Medical Orchestra',
    description:
      'A bespoke redesign for a Durham, NC community orchestra of health professionals and life-science students, built on the Fugue Lab CMS-editable platform.',
    image: '/portfolio/durham-medical-orchestra.jpg',
    url: 'https://durhammedicalorchestra.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'Durham Medical Orchestra (Demo Preview)',
    summary:
      'The Durham Medical Orchestra draws together health-related professionals, medical and graduate students of the life sciences, and community members in Durham, North Carolina. Founded in 2010 and affiliated with the Vice Provost for the Arts at Duke University, the all-volunteer orchestra performs free concerts across the Triangle. We rebuilt their site around a single editorial idea, "where medicine meets music," to spotlight their free concerts and high-engagement concerto competition.',
    objectives: [
      'Lead with the orchestra’s distinctive identity, health professionals making music, instead of a generic community-ensemble template',
      'Make the free-admission concert season and full program details easy to find and share',
      'Give the Concerto Competition a clear, scannable two-round structure with eligibility and requirements',
      'Surface tax-deductible donation paths and 501(c)(3) Foundation details to drive giving',
      'Hand the all-volunteer committee a site they can edit themselves through the Fugue Lab CMS',
    ],
    implementation: [
      'Custom "where medicine meets music" editorial theme: clinical paper, deep petrol-teal ink, and a warm brass accent used as punctuation',
      'Spectral display type paired with IBM Plex Sans body, plus an asymmetric split editorial hero with a hard-cropped B&W conductor photo',
      'Signature EKG-to-staff hairline that begins as a heartbeat waveform and resolves into a five-line musical staff, reused across the site',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'CMS content seam via useSite() with each block independently falling back to compiled-in static content',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap and robots',
      'SES-or-mailto contact form across seven routes: Home, About, Concerts, Concerto Competition, Musicians, Support, Contact',
    ],
    results: [
      'Seven fully prerendered routes scoring 97/100 (A) on the rendered quality audit',
      'Accessibility, SEO, content, and structured data all at 100 on every page',
      'A distinctive, on-brand identity that reads as a clinical-arts orchestra rather than a stock template',
      'A CMS-editable platform the volunteer committee can update without touching design or code',
    ],
    metrics: [
      {
        value: '7',
        label: 'Pages',
        description: 'Home, About, Concerts, Concerto Competition, Musicians, Support, Contact',
      },
      { value: '97/100', label: 'Quality Score', description: 'Rendered A grade across all routes' },
      { value: '1 day', label: 'Built In', description: 'Automated demo site pipeline' },
    ],
  },
  {
    slug: 'youth-orchestras-of-charlotte',
    title: 'Youth Orchestras of Charlotte',
    description:
      'A redesigned site for a Charlotte youth orchestra program: four ensembles, the real season calendar, audition info, and an international tour, all server-rendered for SEO.',
    image: '/portfolio/youth-orchestras-of-charlotte.jpg',
    url: 'https://youthorchestrasofcharlotte.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'Youth Orchestras of Charlotte (Demo Preview)',
    summary:
      'The Youth Orchestras of Charlotte are an independent nonprofit dedicated to nurturing and inspiring the musical talents of youth in the greater Charlotte area, training roughly 200 musicians ages 9-19 across four ensembles led by Charlotte Symphony players and the region’s top music educators. This bespoke demo rebuilds their site as a fast, server-rendered experience: a poster-style collage hero, a crimson ensemble tile grid, the real 2025-26 season calendar, consolidated audition info, and the orchestra’s first international tour. Every fact, bio, and tuition figure comes verbatim from the org’s own site.',
    objectives: [
      'Make four distinct ensembles (Youth Orchestra, Preparatory, Sinfonia Strings, Flute Choir) easy to navigate',
      'Get concerts and events into Google-indexable HTML instead of an AJAX-invisible calendar widget',
      'Consolidate audition requirements, tuition, and the registration flow onto one SEO-titled page',
      'Add email capture, which the original site lacked entirely',
      'Give the Austria 2026 tour and donation paths prominent, working CTAs',
    ],
    implementation: [
      'Bespoke Vite + React 18 + Tailwind 4 build (not the CMS template), cloning the Samuel Sparrow scaffold',
      'Server-rendered per-route via an SSR entry bundle, so events and bios ship as crawlable HTML with per-page titles and meta',
      'Concert-poster type system: Fraunces display + Montserrat body, crimson on warm-ivory with near-black "stage" sections',
      'Signature crimson-tinted clickable ensemble tile grid on Home and Programs',
      'Seven routes (Home, About, Programs, Events, Auditions, Austria Tour, Support) with the real 2025-26 season and verbatim staff bios',
      'Front-end newsletter signup plus live donate (DonorPerfect), audition register (JotForm), and ticket (Etix) CTAs',
      'Optimized WebP images synced from S3, with site.ts as the single source of truth populated verbatim from scraped content',
    ],
    results: [
      'Events and ensemble content now render in server-side HTML with a sitemap and per-page meta, fixing the original calendar’s invisibility to search',
      'Added an email-capture section the original site never had',
      'Auditions, tuition, and registration unified on one searchable page with a working register CTA',
      'Four-ensemble program structure and the Austria tour made clear and scannable in a single, cohesive design',
    ],
    metrics: [
      { value: '7', label: 'Pages', description: 'Home, About, Programs, Events, Auditions, Austria Tour, Support' },
      { value: '4', label: 'Ensembles', description: '~200 musicians ages 9-19' },
      { value: 'Hours', label: 'Built In', description: 'Automated demo site pipeline' },
    ],
  },
  {
    slug: 'oneal-pipe-organs',
    title: 'O’Neal Pipe Organs',
    description:
      'A cinematic "workshop noir" site for a Southern California pipe organ builder and restorer, rebuilt from a flat, HTTP-only Weebly site.',
    image: '/portfolio/oneal-pipe-organs.jpg',
    url: 'https://onealpipeorgans.fuguelab.app',
    clientTypes: [ClientType.Organization],
    client: 'O’Neal Pipe Organs (Demo Preview)',
    summary:
      'O’Neal Pipe Organs, Inc. has built, restored, and maintained pipe organs across greater Los Angeles and Orange Counties and central Arizona since 1995, serving every major religious denomination and several educational institutions. Their original site was a flat, caption-less Weebly that redirected HTTPS down to insecure HTTP. We rebuilt it into a five-page, fully CMS-editable showcase that turns their workshop and installation photography into the centerpiece, with all body copy verbatim from their own site.',
    objectives: [
      'Replace the insecure HTTPS-to-HTTP Weebly site with a fast, secure, professionally designed presence',
      'Turn dozens of uncaptioned workshop and installation photos into a browsable, lightboxed portfolio of their craft',
      'Organize nine flat service bullets into three clear, photo-led service families',
      'Reinforce 30 years of credibility and service area without inventing any copy',
      'Make every page owner-editable in the Fugue Lab CMS so the owner can update content without a developer',
    ],
    implementation: [
      'Bespoke Vite + React SSR build reading published content from the Fugue Lab prod CMS, with compiled-in static content as a no-CMS fallback',
      '"Workshop noir" theme: brass on warm paper with a dark-walnut surface-invert for navbar, footer, and editorial bands; Fraunces display + Inter body',
      'Per-org custom CSS layering a paper-grain overlay, display type scale, and brass selection that the style config alone cannot express',
      'Cinematic home page: word-reveal hero, blur-reveal intro, clickable walnut service triptych, and a full-bleed sanctuary parallax',
      'GSAP/Lenis scroll animations wired through the CMS animation enum (fade, slide-up, blur-reveal, stagger, parallax), reduced-motion aware',
      'Shared @gjl/cms-components sections: hero, richtext, gallery with per-image link and brand-tint overlay, image, form, and Google Maps embed',
      '55-photo "Our Work" gallery with uniform tiles, scale-hover, and lightbox; all imagery served as sized WebP with JPEG originals retained',
    ],
    results: [
      'Live, secure, CMS-editable five-page site graded A (96-97) across all routes',
      'Flat nine-item service list reorganized into three photo-led families presenting all nine services verbatim',
      'A formerly caption-less photo dump became a 55-image lightboxed portfolio that showcases the actual craftsmanship',
      'Shipped reusable platform upgrades (gallery per-image links/overlay tints, an invert background token, section-animation wrapper) that benefit every CMS site',
    ],
    metrics: [
      { value: '5', label: 'Pages', description: 'Home, About Us, Services, Our Work, Contact' },
      { value: '30+', label: 'Years in Business', description: 'Building and restoring pipe organs since 1995' },
      { value: '<1 day', label: 'Built In', description: 'Automated demo site pipeline' },
    ],
  },
  {
    slug: 'raleigh-music-collective',
    title: 'The Raleigh Music Collective',
    description:
      'A bright, joyful redesign for an El Sistema inspired youth string-education nonprofit in Raleigh, NC, built on the Fugue Lab CMS-editable platform with their own hero video preserved.',
    image: '/portfolio/raleigh-music-collective.jpg',
    url: 'https://raleighmusiccollective.fuguelab.app',
    clientTypes: [ClientType.Organization, ClientType.NonProfit],
    client: 'The Raleigh Music Collective (Demo Preview)',
    summary:
      'The Raleigh Music Collective is an El Sistema inspired nonprofit that gives children from refugee, immigrant, and low-income families a tuition-free path into music, from first-year string classes at a community hub up to full núcleo orchestras and one-on-one lessons. We rebuilt their site to look the way the work feels, bright and warm and hopeful, with the waitlist and a clear way to give one tap from the homepage and their own hero video kept front and center.',
    objectives: [
      'Carry the program’s “Creativity, Dignity, and Joy” spirit into a bright, photo-rich site instead of a tidy but plain template',
      'Put the waitlist, lessons signup, and a clear way to give one tap from the homepage',
      'Make the three program paths (núcleo orchestras, community-hub classes, lessons studio) and the summer camp easy to follow',
      'Fix accessibility so a tuition-free, serve-all-people program lives up to that promise online',
      'Give the small team a site they can keep current themselves through the Fugue Lab CMS',
    ],
    implementation: [
      'Bright sunlit-ivory surface with deep teal ink and a marigold and coral polychrome accent, the opposite of the dark-stage register used for other education demos',
      'Bricolage Grotesque paired with Hanken Grotesk, a light editorial split hero, and a “collective” dot-cluster signature of many students gathering into one ensemble',
      'The org’s real Squarespace hero video scraped and re-encoded to web-weight, served device-responsively (desktop and mobile sources) and poster-backed',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'CMS content seam via useSite() with each block independently falling back to compiled-in static content',
      'SSR/hydration with per-route static prerendering (SSG) plus generated sitemap, robots, and structured data',
      'Twelve routes plus standard privacy and accessibility pages',
    ],
    results: [
      'A 61-to-97 (A) rendered quality score, up from a failing grade on the current site',
      'Accessibility lifted from 0 to 100, the one place the old site fell short of its serve-all-people promise',
      'Their own hero video preserved and shown front and center on a fast, phone-friendly home',
      'A CMS-editable platform the team can update without touching design or code',
    ],
    metrics: [
      {
        value: '12',
        label: 'Pages',
        description:
          'Home, Who We Are, Our Story, Orchestral Program, Lessons, Summer Camp, Staff, Teaching Artists, Partners, Get Involved, Gallery, Contact',
      },
      { value: '97/100', label: 'Quality Score', description: 'Rendered A grade, up from a failing current site' },
      { value: '<1 day', label: 'Built In', description: 'Bespoke build on the Fugue Lab platform' },
    ],
  },
  {
    slug: 'classically-minded',
    title: 'Classically Minded',
    description:
      'A blog-style digital publication dedicated to classical music thought leadership. Built for long-term SEO, writer-friendly content workflows, and clean, reader-focused design.',
    image: '/portfolio/classically-minded.png',
    url: 'https://classicallyminded.org',
    clientTypes: [ClientType.Publication],
    client: 'Fugue Lab (Internal Publication)',
    summary:
      'Created a digital publication platform for classical music thought leadership. The site is optimized for long-term organic search growth, establishing authority in the classical music space while demonstrating content strategy expertise to prospective clients.',
    objectives: [
      'Build organic search presence over time',
      'Establish thought leadership in classical music',
      'Demonstrate content marketing capabilities',
      'Create a writer-friendly publishing workflow',
      'Design a clean, reader-focused experience',
    ],
    implementation: [
      'SEO-optimized article structure and metadata',
      'Fast-loading static site architecture',
      'Category and tag organization for content discovery',
      'Social sharing integration',
      'Mobile-first responsive design',
      'Clean typography optimized for long-form reading',
    ],
    results: [
      'Growing organic search traffic month over month',
      'Establishing authority in classical music digital space',
      'Content portfolio demonstrating expertise to clients',
      'Foundation for long-term inbound marketing',
    ],
    metrics: [
      { value: 'SEO', label: 'Focus', description: 'Long-term organic growth strategy' },
      { value: '100%', label: 'Static', description: 'Fast-loading architecture' },
    ],
  },
  {
    slug: 'holy-trinity-covina',
    title: 'Holy Trinity Covina – Music Ministry',
    description:
      'A parish site for an Episcopal church in LA County featuring music archives, choir schedules, giving integration, and custom event sections tailored to liturgical needs.',
    image: '/portfolio/holy-trinity.jpeg',
    url: 'https://holytrinitycovina.com',
    clientTypes: [ClientType.Church],
    client: 'Holy Trinity Episcopal Church, Covina',
    summary:
      'Redesigned the church website with a focus on music ministry, making it easy for parishioners to access service music, choir schedules, and donate online. The site serves as both a community hub and an outreach tool for visitors exploring the parish.',
    objectives: [
      'Showcase the vibrant music ministry program',
      'Provide easy access to service music and liturgical resources',
      'Enable online giving with integrated donation platform',
      'Create a welcoming digital presence for visitors',
    ],
    implementation: [
      'Custom WordPress theme with liturgical calendar integration',
      'Music archive with searchable hymn and anthem database',
      'Online giving integration with Tithe.ly',
      'Event management for concerts and special services',
      'Mobile-responsive design for on-the-go parishioners',
    ],
    results: [
      'Online giving increased with Tithe.ly integration',
      'Choir members access service music digitally instead of paper bulletins',
      'Event calendar reduces weekly email load for parish office',
      'Professional web presence attracting new visitors and parishioners',
    ],
    metrics: [
      { value: '13 years', label: 'A Church Musician', description: 'Built by their own working organist' },
      { value: 'Online', label: 'Giving', description: 'Tithe.ly integration' },
      { value: 'Digital', label: 'Music Archive', description: 'Searchable hymn database' },
    ],
    testimonial: {
      quote:
        'Having a real website changed how visitors see us. The music archive alone saves me hours each week planning services.',
      author: 'Garrett John Law',
      role: 'Director of Music, Holy Trinity Covina',
    },
  },
  {
    slug: 'kaia-string-quartet',
    title: 'KAIA String Quartet',
    description:
      'Custom site for a vibrant, all-female Chicago-based string quartet. Features include concert listings, ensemble media, education services, and an embedded email list.',
    image: '/portfolio/kaia-string-quartet.jpg',
    url: 'https://kaiastringquartet.com',
    clientTypes: [ClientType.Quartet, ClientType.Ensemble],
    client: 'KAIA String Quartet',
    summary:
      'Created a dynamic website for a Chicago-based all-female string quartet, showcasing their artistry, upcoming performances, and educational outreach programs. The site serves as a professional hub for booking inquiries and audience engagement.',
    objectives: [
      'Establish a professional online presence for the quartet',
      'Showcase performance videos and recordings',
      'Promote educational outreach and workshop services',
      'Enable easy booking inquiries for presenters',
      'Build an engaged audience through email list',
    ],
    implementation: [
      "Custom responsive design reflecting the quartet's brand identity",
      'Integrated video gallery for performance highlights',
      'Concert calendar with upcoming event listings',
      'Education services page detailing workshop offerings',
      'Mailchimp integration for newsletter signups',
      'Contact form for booking and collaboration inquiries',
    ],
    results: [
      'Professional platform for presenter outreach',
      'Increased visibility for educational programs',
      'Growing email list of engaged audience members',
      'Streamlined booking inquiry process',
    ],
  },
  {
    slug: 'jonathan-gregoire',
    title: 'Jonathan Gregoire – Creative Coach',
    description:
      'Sales-focused landing page for a creative coach and organist. Highlights include branding, lead capture, and targeted CTAs to convert visitors into clients.',
    image: '/portfolio/jonathan-gregoire.jpg',
    url: 'https://jonathangregoire.com',
    clientTypes: [ClientType.Coach, ClientType.Soloist],
    client: 'Jonathan Gregoire',
    summary:
      'Designed a conversion-focused landing page for a creative coach and organist. The site positions Jonathan as an authority in his niche while driving coaching inquiries through strategic CTAs and lead capture.',
    objectives: [
      'Establish professional brand positioning',
      'Increase coaching inquiries and bookings',
      'Capture leads for email nurturing',
      'Showcase dual expertise as coach and musician',
      'Create a clear path to booking',
    ],
    implementation: [
      'Conversion-optimized landing page structure',
      'Strategic call-to-action placement',
      'Lead capture form integration',
      'Professional brand photography integration',
      'Mobile-responsive design',
      'Fast-loading single-page architecture',
    ],
    results: [
      'Increased coaching inquiry volume',
      'Professional platform for brand positioning',
      'Streamlined booking process for clients',
      'Foundation for ongoing marketing efforts',
    ],
    metrics: [
      { value: '1', label: 'Page', description: 'Focused landing page design' },
      { value: 'Lead Gen', label: 'Focus', description: 'Conversion-optimized CTAs' },
    ],
  },
  {
    slug: 'abigayle-flack',
    title: 'Abigayle Flack – Soprano',
    description:
      'Personal portfolio site for a professional soprano. Includes performance video samples, biography, and contact section for bookings.',
    image: '/portfolio/abigayle-flack.jpg',
    url: 'https://abigayleflack.com',
    clientTypes: [ClientType.Soloist, ClientType.Vocalist],
    client: 'Abigayle Flack',
    summary:
      'Created an elegant portfolio site for a professional soprano, providing a polished digital presence for auditions, competitions, and booking inquiries. The clean design lets her artistry speak for itself.',
    objectives: [
      'Establish professional presence for auditions and competitions',
      'Showcase performance videos and recordings',
      'Provide easy access for presenters and casting directors',
      'Enable booking inquiries',
    ],
    implementation: [
      'Clean, elegant design fitting classical music aesthetic',
      'Embedded video and audio samples',
      'Professional biography and repertoire',
      'Contact form for booking inquiries',
      'Mobile-responsive for on-the-go viewing',
    ],
    results: [
      'Professional platform for audition materials',
      'Centralized hub for media and credentials',
      'Streamlined booking inquiry process',
    ],
    metrics: [{ value: '< 2 weeks', label: 'Turnaround', description: 'Quick delivery' }],
  },
  {
    slug: 'susan-bengtson-price',
    title: 'Dr. Susan Bengtson Price – Viola & Teaching',
    description:
      'Professional portfolio for a violist and music educator. Includes CV, media clips, teaching philosophy, and student testimonials.',
    image: '/portfolio/susan-bengtson.jpg',
    url: 'https://www.susanbengtson.com/',
    clientTypes: [ClientType.Soloist, ClientType.Educator],
    client: 'Dr. Susan Bengtson Price',
    summary:
      'Built a comprehensive portfolio for a violist and music educator, balancing performance credentials with teaching philosophy. The site serves dual purposes: attracting performance opportunities and prospective students.',
    objectives: [
      'Showcase dual career as performer and educator',
      'Present academic credentials and CV',
      'Highlight teaching philosophy and student success',
      'Provide media samples for presenters',
      'Attract prospective students',
    ],
    implementation: [
      'Clean, elegant design with academic polish',
      'Structured CV and credentials section',
      'Teaching philosophy and studio information',
      'Embedded performance media',
      'Mobile-responsive design',
      'Contact integration for inquiries',
    ],
    results: [
      'Professional platform for academic and performance careers',
      'Centralized hub for credentials and media',
      'Clear pathways for students and presenters',
    ],
    metrics: [{ value: '< 2 weeks', label: 'Turnaround', description: 'Quick delivery' }],
  },
  {
    slug: 'matt-mueller',
    title: 'Matt Mueller – Baritone',
    description:
      'Clean and elegant site for a baritone singer, featuring performance media, repertoire list, and professional biography.',
    image: '/portfolio/matt-mueller.webp',
    url: 'https://www.mattmmueller.com/',
    clientTypes: [ClientType.Soloist, ClientType.Vocalist],
    client: 'Matt Mueller',
    summary:
      'Designed an elegant portfolio site for a professional baritone, optimized for opera companies, concert presenters, and casting directors reviewing audition materials.',
    objectives: [
      'Create professional presence for auditions',
      'Showcase performance videos and recordings',
      'Present repertoire and biography',
      'Enable easy access for casting directors',
    ],
    implementation: [
      'Clean, elegant design for classical music aesthetic',
      'Embedded video and audio samples',
      'Comprehensive repertoire list',
      'Professional biography and press materials',
      'Mobile-responsive for on-the-go viewing',
      'Fast-loading architecture',
    ],
    results: [
      'Professional digital presence for audition process',
      'Centralized media hub for presenters',
      'Streamlined access to credentials and samples',
    ],
    metrics: [{ value: '< 2 weeks', label: 'Turnaround', description: 'Quick delivery' }],
  },
  {
    slug: 'david-heinze',
    title: 'David Heinze – Organist',
    description:
      'Digital calling card for a professional organist. Includes audio/video samples, upcoming recitals, and church music service information.',
    image: '/portfolio/david-heinze.jpg',
    url: 'https://davidsheinze.com',
    clientTypes: [ClientType.Organist, ClientType.Soloist],
    client: 'David Heinze',
    summary:
      'Created a professional digital presence for a concert organist, showcasing recital recordings, upcoming performances, and church music services. The site serves as a hub for both concert presenters and churches seeking organ services.',
    objectives: [
      'Establish professional concert organist presence',
      'Showcase recital recordings and videos',
      'Promote upcoming performances',
      'Present church music service offerings',
      'Enable booking inquiries',
    ],
    implementation: [
      'Clean, elegant design fitting organ repertoire aesthetic',
      'Embedded audio and video samples',
      'Upcoming recitals and event listings',
      'Church music services information',
      'Contact form for booking inquiries',
      'Mobile-responsive design',
    ],
    results: [
      'Professional platform for concert career',
      'Centralized hub for recordings and credentials',
      'Clear presentation of service offerings',
    ],
    metrics: [{ value: '< 2 weeks', label: 'Turnaround', description: 'Quick delivery' }],
  },
  {
    slug: 'danielle-dinstman',
    title: 'Danielle Dinstman – Visual Artist',
    description:
      'Portfolio site for a Los Angeles painter, designer, and educator. A gallery-wall design with a full lightbox, titled paintings, and grouped graphic-design case studies.',
    image: '/portfolio/danielledinstman.jpg',
    url: 'https://danielledinstman.dev.fuguelab.app',
    clientTypes: [ClientType.Artist, ClientType.Educator],
    client: 'Danielle Dinstman',
    summary:
      'Rebuilt a multidisciplinary visual artist’s portfolio as a bespoke “gallery wall”: work hung in matted hairline frames with museum captions, opening into a full-screen lightbox. Painting, photography, illustration, and six graphic-design case studies each get their own room, with her real titles, mediums, and project write-ups recovered from the original site.',
    objectives: [
      'Give the work a gallery-quality presentation that beats the original template',
      'Show titles and details (medium, size, year) on every painting',
      'Let visitors view any piece full-screen and browse with prev/next',
      'Group the graphic-design work into per-project case studies',
      'Make the whole site editable and crawlable, fast on any device',
    ],
    implementation: [
      'Bespoke Vite + React + Tailwind build on the Fugue Lab platform',
      'Fraunces + Inter type on warm gallery paper with a single oxblood accent',
      'Masonry “gallery wall” that respects each work’s true aspect ratio',
      'Full-screen lightbox with keyboard and button prev/next navigation',
      'Per-project design tabs (NAEA, Gisou, Mark Bradford, CatCon, National Parks, Sierra Club)',
      'Prerendered SSG for crawlable pages, per-route SEO, and a sitemap',
      'WCAG-AA accessibility and a 99–100/100 quality score across the site',
    ],
    results: [
      'Painting, photography, illustration, and design each presented as their own body of work',
      'Every painting now shows its real title and details; every project its own case study',
      'A polished, gallery-quality site that loads fast and reads cleanly on mobile',
    ],
    metrics: [
      {
        value: '8',
        label: 'Pages',
        description: 'Home, Painting, Photography, Illustration, Design, CV + 2 exhibitions',
      },
      { value: '145+', label: 'Works', description: 'Paintings, photography, illustration, and design case studies' },
      { value: 'A', label: 'Quality', description: '99–100/100 on the Fugue Lab quality gate' },
    ],
  },
  // {
  //   slug: 'patrick-pope',
  //   title: 'Dr. Patrick Pope – Organist & Composer',
  //   description:
  //     'Comprehensive portfolio for a church musician and composer, showcasing compositions, recordings, and liturgical music credentials.',
  //   image: '/portfolio/patrick-pope.jpg',
  //   url: 'https://patrickpopeorganist.com',
  //   clientTypes: [ClientType.Organist],
  // },
  {
    slug: 'music-garrett-john-law',
    title: 'Garrett John Law – Organist & Conductor',
    description:
      'A modern, personal website for a professional church musician. Built to showcase media, performances, credentials, and service information with elegance and clarity.',
    image: '/portfolio/garrett.webp',
    url: 'https://music.garrettjohnlaw.com',
    clientTypes: [ClientType.Organist, ClientType.Conductor],
    client: 'Garrett John Law (Founder)',
    summary:
      "Built on the Fugue Lab platform, this site showcases the founder's own music career as an organist and conductor. Demonstrates the same approach and quality we bring to client projects.",
    objectives: [
      'Showcase organ and choral conducting work',
      'Present professional credentials and experience',
      'Highlight performance media and recordings',
      'Demonstrate Fugue Lab platform capabilities',
    ],
    implementation: [
      'Built on Fugue Lab platform (eating our own cooking)',
      'Clean, elegant design for sacred music aesthetic',
      'Embedded video and audio samples',
      'Event listings and performance calendar',
      'Professional biography and credentials',
      'Mobile-responsive design',
    ],
    results: [
      'Professional platform for music career',
      'Live demonstration of Fugue Lab capabilities',
      'Proof of concept for platform features',
    ],
    metrics: [{ value: 'Platform', label: 'Built On', description: 'Fugue Lab CMS' }],
  },
  {
    slug: 'st-georges-belfast',
    title: 'St George’s Parish Church, Belfast',
    description:
      'A bespoke site for a historic Belfast city-centre parish with a choral tradition renowned throughout Ireland, replacing a graded-D template with a candlelit, CMS-editable home.',
    image: '/portfolio/st-georges-belfast.jpg',
    url: 'https://stgeorgesbelfast.fuguelab.app',
    clientTypes: [ClientType.Church, ClientType.NonProfit],
    client: 'St George’s Parish Church, Belfast (Demo Preview)',
    summary:
      'St George’s is a High Church (Anglo-Catholic) parish of the Church of Ireland in Belfast city centre, worshipping on the site where the city itself began since 1816. Its music, led by Director of Music David Falconer, carries one of the last men-and-boys choirs in Ireland alongside a Female Voice Choir, a Chamber Choir, and an historic organ. This bespoke demo lifts the parish out of a template site graded D and gives its worship, music, history, and outreach a dignified, current home, every section editable in the Fugue Lab CMS.',
    objectives: [
      'Carry the dignity of a 200-year-old Georgian church, not a generic church template',
      'Put the renowned choral tradition and the historic organ front and centre',
      'Fix the crawlability gaps the old site had: no sitemap, no structured data, blocked zoom',
      'Hand the parish a site the office can keep current itself',
    ],
    implementation: [
      'Custom “open door in the heart of the city” theme: cool Portland-stone surface, stained-glass sapphire, and antique gilt, with candlelit feature bands',
      'Signature Cinzel inscriptional Roman capitals over a gilt hairline, carved-stone lettering echoing the 1788 Corinthian portico',
      'A split candlelit hero pairing a solid text panel with the parish choir by candlelight',
      'Content drawn verbatim from the parish’s own pages (three choirs, the organ appeal, the 1816 history, outreach, clergy and music staff)',
      'Vite + React 18 + Tailwind CSS 4 wired to @gjl/site-kit as a bespoke-but-CMS-editable site',
      'SSR/hydration with per-route static prerendering (SSG), generated sitemap, robots, and JSON-LD',
      'Thirteen routes: Home, Worship, Music, The Organ, History, Events, Community, People, Support, Join, Contact, plus Privacy and Accessibility',
    ],
    results: [
      'Thirteen fully prerendered routes scoring 99/100 (A) on the rendered quality audit, with accessibility and SEO at 100',
      'A reverent, historic identity that finally does justice to the choral tradition',
      'Structured data, social-share tags, and a sitemap the graded-D site lacked',
      'A CMS-editable platform the parish office can update without touching design or code',
    ],
    metrics: [
      { value: '1816', label: 'Heritage', description: 'Worshipping on this site for over two centuries' },
      { value: '99/100', label: 'Quality', description: 'Rendered grade (A), accessibility + SEO at 100' },
      {
        value: '13',
        label: 'Pages',
        description: 'Worship, Music, Organ, History, Events, Community, People, Support, Join, Contact + legal',
      },
    ],
  },
]
